import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProductCard from './ProductCard';
import { request } from '../utils/request';
import { Product as MockProduct, Category } from '../data/mockData';
import {
  Squares2X2Icon,
  ListBulletIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { applyResourcesToProducts } from '../utils/designUtils';
import { subscribeToResources, getGlobalResources, updateGlobalResources, type Resource } from '../utils/globalState';
import { ApiProduct, ApiTemplate } from '../utils/resourceUtils';
import { ProductsResponse } from '../utils/resourceUtils';
import { baseImageToOss } from '../utils/common';
import { OSS_BUCKET, OSS_BASE_URL } from '../config';

const ProductList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<string>('name');
  const [resources, setResources] = useState(getGlobalResources());
  const [products, setProducts] = useState<MockProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const type = searchParams.get('type') || 'product';
  const tag = searchParams.get('tag');
  const productId = searchParams.get('productId');

  // Load products from API
  const loadProducts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (category) {
        params.append('category', category);
      }

      if (search) {
        params.append('search', search);
      }

      if (type) {
        params.append('type', type);
      }

      if (tag) {
        params.append('tag', tag);
      }

      if (productId) {
        params.append('productId', productId);
      }

      // Map frontend sort options to backend sort options
      let sortByParam = 'name';
      let sortOrderParam = 'ASC';

      switch (sortBy) {
        case 'price-low':
          sortByParam = 'basePrice';
          sortOrderParam = 'ASC';
          break;
        case 'price-high':
          sortByParam = 'basePrice';
          sortOrderParam = 'DESC';
          break;
        case 'name':
        default:
          sortByParam = 'name';
          sortOrderParam = 'ASC';
          break;
      }

      params.append('sortBy', sortByParam);
      params.append('sortOrder', sortOrderParam);

      const response: ProductsResponse = await request({
        url: `products?${params.toString()}`,
        method: 'GET'
      });

      if (response.success) {
        // Transform API products to match frontend Product interface
        const transformedProducts: MockProduct[] = response.result.products.map((apiProduct: ApiProduct) => ({
          id: apiProduct.id.toString(),
          name: apiProduct.name,
          description: apiProduct.description || '',
          category: apiProduct.category,
          price: apiProduct.basePrice || apiProduct.price || 0,
          image: apiProduct.image || '',
          rating: apiProduct.rating || 0,
          reviews: apiProduct.reviews || 0,
          sliderImages: apiProduct.sliderImages || '',
          optionCombinations: apiProduct.optionCombinations || [],
          customizable: true, // Default to true for API products
          templates: (apiProduct.templates || []).map((template: ApiTemplate) => ({
            id: template.id.toString(),
            name: template.name,
            preview: '', // API doesn't provide preview, use empty string
            design: template.design
          })),
          colors: [], // Default empty colors array
          sizes: [], // Default empty sizes array
          previews: apiProduct.previews || []
        }));
        setProducts(transformedProducts);
        setPagination(response.result.pagination);
      } else {
        setError(response.message || 'Failed to load products');
      }
    } catch (error: any) {
      console.error('Load products error:', error);
      setError('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response: any = await request({
        url: 'categories',
        method: 'GET'
      });

      if (response.success) {
        setCategories(response.result);
      }
    } catch (error) {
      console.error('Load categories error:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load products when component mounts or dependencies change
  useEffect(() => {
    loadProducts();
  }, [category, search, sortBy, pagination.page, tag, productId]);

  // 如果 URL 中存在 image 参数：清空并用该图片初始化全局资源
  useEffect(() => {
    const imageUrl = searchParams.get('image');
    if (imageUrl) {
      setIsLoading(true);

      const processImage = async () => {
        try {
          let newCloudUrl = '';

          // If URL already contains aliyuncs.com, use it directly
          if (imageUrl.includes('aliyuncs.com')) {
            newCloudUrl = imageUrl;
          } else {
            // Otherwise fetch and upload via backend
            const response: any = await request({
              method: 'POST',
              url: 'images/fetch',
              data: { url: imageUrl }
            });

            if (response.success && response.result) {
              newCloudUrl = response.result.url;
            } else {
              console.error('Failed to upload image via backend:', response.message);
              setIsLoading(false);
              return;
            }
          }
 

          const initialResources = [
            {
              id: `url-${Date.now()}`,
              type: 'image' as const,
              width: searchParams.get('width') ? parseInt(searchParams.get('width') || '0') : 0,
              height: searchParams.get('height') ? parseInt(searchParams.get('height') || '0') : 0,
              content: newCloudUrl,
              name: 'URL Image',
              focus: true,
              order: 1
            }
          ]; 
          // Get current resources to append to
          const currentResources = getGlobalResources();
          const updatedResources = [...initialResources, ...currentResources];
          
          updateGlobalResources(updatedResources);
          setResources(updatedResources);
          setIsLoading(false);
        } catch (err) {
          console.error('Failed to process image from URL:', err);
          setIsLoading(false);
        }
      };

      processImage();
    }
  }, [searchParams]);

  // 监听 Navbar 中的 onChangeResource 事件
  useEffect(() => {
    const handleResourceChange = (newResources: any[]) => {
      console.log('ProductList 监听到资源变化:', newResources);
      // 如果newResources数组中的对象type=="image"并且没有width和height下标，就移除
      const validResources = newResources.filter((r) => {
        if (r.type === 'image') {
          return r.width !== undefined && r.height !== undefined;
        }
        return true;
      });
      setResources(validResources);
    };

    // 订阅全局资源变化
    const unsubscribe = subscribeToResources(handleResourceChange);

    // 清理订阅
    return () => {
      console.log('ProductList 清理订阅');
      unsubscribe();
    };
  }, [resources]);

  // 应用资源到产品的图层
  const productsWithUpdatedLayers = applyResourcesToProducts(products, resources);

  // Products are already filtered and sorted by the API 
  const sortedProducts = productsWithUpdatedLayers;

  // Get category name for display - dynamically from API loaded categories
  const getCategoryName = (categoryId: string) => {
    // Normalize categoryId to string for comparison
    const normalizedId = categoryId.toString();

    // First, try to find in main categories by ID
    const mainCategory = categories.find(c => c.id.toString() === normalizedId);
    if (mainCategory) {
      return mainCategory.name;
    }

    // Then, try to find in subcategories by href or name
    for (const category of categories) {
      if (category.subCategories) {
        for (const subCategory of category.subCategories) {
          // Extract category ID from href (e.g., /products?category=123 -> 123)
          const subId = subCategory.id;

          // Match by extracted ID or by checking if href contains the categoryId
          if (subId === normalizedId ||
            subCategory.href.includes(normalizedId)) {
            return subCategory.name;
          }
        }
      }
    }

    // If not found, try to find by name match (case-insensitive)
    const categoryByName = categories.find(c =>
      c.name.toLowerCase() === categoryId.toLowerCase()
    );
    if (categoryByName) {
      return categoryByName.name;
    }

    // Fallback to categoryId
    return categoryId;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {category ? getCategoryName(category) : 'All Products'}
          </h1>
          <p className="text-gray-600">
            {category
              ? `Discover and customize your perfect ${getCategoryName(category).toLowerCase()} design`
              : 'Discover and customize your perfect design'
            }
          </p>
        </div>

        <div className="w-full">
          {/* Product Grid */}
          <div className="w-full">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {pagination.total} products found
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  {/* Sort Options */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="name">Name A-Z</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>

                  {/* View Mode Toggle */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg ${viewMode === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'
                        }`}
                      style={viewMode === 'grid' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                    >
                      <Squares2X2Icon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg ${viewMode === 'list' ? '' : 'text-gray-400 hover:text-gray-600'
                        }`}
                      style={viewMode === 'list' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                    >
                      <ListBulletIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#2e3a47' }}></div>
                <p className="text-gray-600">Loading products...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Error</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={loadProducts}
                  className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Products */}
            {!isLoading && !error && viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {sortedProducts.map((product, index) => (
                  <ProductCard
                    key={index + 1}
                    index={index + 1}
                    product={product}
                    viewMode="grid"
                    hasResource={resources.length > 0}
                  />
                ))}
              </div>
            )}

            {!isLoading && !error && viewMode === 'list' && (
              <div className="space-y-4">
                {sortedProducts.map((product, index) => (
                  <ProductCard
                    key={index + 1}
                    index={index + 1}
                    product={product}
                    viewMode="list"
                    hasResource={resources.length > 0}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && sortedProducts.length === 0 && (
              <div className="text-center py-12">
                <MagnifyingGlassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters to find what you're looking for.
                </p>
                <button
                  onClick={loadProducts}
                  className="font-medium transition-colors"
                  style={{ color: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#2e3a47'}
                >
                  Refresh
                </button>
              </div>
            )}

            {/* Pagination */}
            {!isLoading && !error && pagination.pages > 1 && (
              <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} products
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-600">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
